// Compositor del chat, como el de Telegram (S10): clip · texto que crece · micrófono que se convierte en
// «enviar» en cuanto hay algo escrito o adjunto. Grabar sustituye la línea (🗑 · tiempo · enviar).
// Enter envía; Mayús+Enter salta de línea — en el móvil (dedo y pantalla estrecha) Enter salta de línea y solo
// envía el botón, como ChatGPT/Telegram en iPhone (09-10). Pegar o soltar un fichero lo adjunta. El borrador se
// guarda por agente en el navegador (borradores.ts): cambiar de chat, recargar o que iOS cierre la PWA no lo pierde.
// En el iPhone se queda encima del teclado y de la barra de inicio (useVisualViewport + safe-area).
//
// Notas de voz (diseño §12.4): el audio sube como adjunto y a la vez se transcribe en el panel; el agente
// recibe la transcripción con el audio adjunto (si no se pudo transcribir, el audio va igual y se dice).
// Una nota NUNCA se pierde: si algo falla queda lista para reintentar o descartar (revisión V1).
import { FileText, Loader2, Mic, Paperclip, ArrowUp, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { postBinario, uploadAttachment } from '../../../lib/api';
import { sendAgentInput } from '../../../hooks/useSocket';
import { mensajeError } from '../errores';
import { BotonIcono, TACTIL_44 } from '../ui';
import { MD_QUERY, useMediaQuery } from '../../../hooks/useMediaQuery';
import { useVisualViewport } from '../../../hooks/useVisualViewport';
import { shouldSendOnEnter } from '../../../lib/composer-keys';
import { guardarBorrador, useBorrador } from './borradores';
import { AVISO_MS, mmss, useGrabadora, type NotaGrabada } from '../voz/useGrabadora';

interface Adjunto {
  chipId: string;
  nombre: string;
  vista: string | null; // miniatura local (imágenes)
  estado: 'subiendo' | 'listo' | 'fallo';
}

const MAX_LINEAS_PX = 200;

/**
 * Esc mientras se graba cancela SOLO la grabación. 29-09: el chat también escucha Esc (lo cierra) en la
 * ventana, y según el orden de registro de los dos oyentes se cerraba el chat entero. Este escucha en la
 * fase de captura (va antes que cualquier oyente normal) y marca el evento como usado: el chat mira
 * `defaultPrevented` y no se cierra. Depende de `cancelar` (estable), no del objeto de la grabadora, que
 * era nuevo en cada render y volvía a registrar el oyente cada vez.
 */
export function useEscCancelaGrabacion(grabando: boolean, cancelar: () => void): void {
  useEffect(() => {
    if (!grabando) return;
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      cancelar();
    };
    window.addEventListener('keydown', tecla, true);
    return () => window.removeEventListener('keydown', tecla, true);
  }, [grabando, cancelar]);
}

// Montado con key={agentId}: nada de lo que hay aquí (texto, adjuntos, nota) puede acabar en otro agente.
// `placeholder` (v7, 01-11): el panel «Tu agente» al lado del chat con Santi dice «Dile algo a tu agente…».
export function Compositor({ agentId, movil = false, placeholder }: { agentId: string; movil?: boolean; placeholder?: string }) {
  const [texto, setTexto] = useBorrador(agentId);
  const [adjuntos, setAdjuntos] = useState<Adjunto[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const selector = useRef<HTMLInputElement>(null);
  const g = useGrabadora(agentId);
  // Teclado en pantalla = dedo Y pantalla de móvil. Un iPad (o un portátil táctil) suele llevar teclado: Enter envía.
  const dedo = useMediaQuery('(pointer: coarse)');
  const anchoEscritorio = useMediaQuery(MD_QUERY);
  const enterSaltaLinea = dedo && !anchoEscritorio;
  // iPhone: con el teclado abierto el compositor sube lo que tapa; cerrado, deja libre la barra de inicio.
  const { keyboardOffset } = useVisualViewport();
  const pie = piePorDebajo(keyboardOffset, movil);

  // 04-11 — al abrir un chat (o pasar a otro con Alt+↓) el cursor ya está en la caja, al final del borrador, como en
  // Mensajes: antes se quedaba en la fila de la lista y cada cambio de agente costaba un clic más (~60 al día). Con el dedo
  // no: abriría el teclado en pantalla sin que Pau lo pida.
  useEffect(() => {
    const el = area.current;
    if (!el || dedo) return;
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
    // Solo al montar (el compositor se monta con key={agentId}: uno por chat abierto).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El área crece con el texto hasta un máximo, como en Telegram.
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_LINEAS_PX)}px`;
  }, [texto]);

  useEscCancelaGrabacion(g.estado === 'grabando', g.cancelar);

  // 29-09: al desmontar se liberan TODAS las miniaturas. Antes el efecto leía `adjuntos` del primer render
  // (vacío): las añadidas después se quedaban ocupando memoria. Una ref lleva siempre la lista de ahora.
  const vistas = useRef<Adjunto[]>([]);
  vistas.current = adjuntos;
  useEffect(() => () => vistas.current.forEach((a) => a.vista && URL.revokeObjectURL(a.vista)), []);

  function adjuntar(ficheros: FileList | File[]) {
    for (const file of Array.from(ficheros)) {
      const chipId = crypto.randomUUID();
      const vista = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      setAdjuntos((xs) => [...xs, { chipId, nombre: file.name, vista, estado: 'subiendo' }]);
      uploadAttachment({ agentId, chipId, messageId: crypto.randomUUID(), file })
        .then(() => setAdjuntos((xs) => xs.map((a) => (a.chipId === chipId ? { ...a, estado: 'listo' } : a))))
        .catch((e: unknown) => {
          setAdjuntos((xs) => xs.map((a) => (a.chipId === chipId ? { ...a, estado: 'fallo' } : a)));
          setError(mensajeError(e));
        });
    }
  }

  function quitar(chipId: string) {
    const vista = adjuntos.find((a) => a.chipId === chipId)?.vista;
    if (vista) URL.revokeObjectURL(vista);
    setAdjuntos((xs) => xs.filter((a) => a.chipId !== chipId));
  }

  const subiendo = adjuntos.some((a) => a.estado === 'subiendo');
  const listos = adjuntos.filter((a) => a.estado === 'listo');
  const hayTexto = texto.trim().length > 0;
  const hayAlgo = hayTexto || listos.length > 0;

  // 04-11 (pelotas, entrega 2): ya no hay «sí» escrito a una tarjeta «Para Santi»: lo único que firma hacia Santi es
  // Enviar en la ventana «Santi». Aquí un mensaje es solo un mensaje al agente.

  async function enviar() {
    if (!hayAlgo || subiendo || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await sendAgentInput(agentId, texto.trim(), listos.length ? listos.map((a) => a.chipId) : undefined, undefined, undefined);
      setTexto('');
      guardarBorrador(agentId, ''); // ya: si iOS cierra la app justo ahora, no vuelve lo enviado
      adjuntos.forEach((a) => a.vista && URL.revokeObjectURL(a.vista));
      setAdjuntos([]);
      area.current?.focus();
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setEnviando(false);
    }
  }

  async function enviarNota(nota: NotaGrabada) {
    setEnviando(true);
    setError(null);
    try {
      const tipo = nota.mime.split(';')[0] ?? 'audio/webm';
      const ext = tipo.includes('mp4') ? 'm4a' : 'webm';
      const duracion = mmss(nota.duracionMs);
      const fichero = new File([nota.audio], `nota-de-voz-${duracion.replace(':', 'm')}s.${ext}`, { type: tipo });
      const chipId = crypto.randomUUID();
      const [subida, transcripcion] = await Promise.allSettled([
        uploadAttachment({ agentId, chipId, messageId: crypto.randomUUID(), file: fichero }),
        postBinario<{ texto: string | null }>('/api/oficina/transcribir', nota.audio, tipo),
      ]);
      const dicho = transcripcion.status === 'fulfilled' ? transcripcion.value.texto?.trim() : null;
      if (!dicho && subida.status !== 'fulfilled') throw new Error('sin audio ni texto');
      const mensaje = dicho
        ? `🎤 Nota de voz de Pau (${duracion}, transcrita automáticamente; puede tener erratas): «${dicho}»`
        : `🎤 Nota de voz de Pau (${duracion}; no se pudo transcribir, el audio va adjunto).`;
      await sendAgentInput(agentId, mensaje, subida.status === 'fulfilled' ? [chipId] : undefined);
      g.olvidar();
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setEnviando(false);
    }
  }

  const alPegar = (e: ClipboardEvent) => {
    if (e.clipboardData.files.length) {
      e.preventDefault();
      adjuntar(e.clipboardData.files);
    }
  };
  const alSoltar = (e: DragEvent) => {
    if (!e.dataTransfer.files.length) return;
    e.preventDefault();
    adjuntar(e.dataTransfer.files);
  };

  // ── Grabando ──
  if (g.estado === 'grabando') {
    return (
      <Marco error={error} pie={pie}>
        <div className="flex min-h-[52px] items-center gap-2 px-2" role="group" aria-label={t('oficinaV2.voz.grabando')}>
          <BotonIcono icono={Trash2} etiqueta={t('oficinaV2.voz.cancelar')} onClick={g.cancelar} />
          <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-oficina-error" aria-hidden />
          <span className={cn('font-mono text-o-cuerpo tabular-nums', g.ms >= AVISO_MS ? 'text-oficina-bloqueado' : 'text-oficina-texto')}>{mmss(g.ms)}</span>
          <span className="min-w-0 flex-1 truncate text-o-texto text-oficina-texto3">
            {g.ms >= AVISO_MS ? t('oficinaV2.voz.casiMaximo') : t('oficinaV2.voz.escCancela')}
          </span>
          <BotonEnviar etiqueta={t('oficinaV2.voz.enviar')} onClick={() => void g.parar().then((n) => n && enviarNota(n))} />
        </div>
      </Marco>
    );
  }

  // ── Nota lista (paró por el máximo o falló el envío): se envía o se descarta; nunca se pierde ──
  if (g.nota) {
    const nota = g.nota;
    return (
      <Marco error={error} pie={pie}>
        <div className="flex min-h-[52px] items-center gap-2 px-2">
          <BotonIcono icono={Trash2} etiqueta={t('oficinaV2.voz.descartar')} onClick={g.olvidar} disabled={enviando} />
          <Mic className="h-4 w-4 shrink-0 text-oficina-enlace" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-o-texto text-oficina-texto">{t('oficinaV2.voz.lista', { duracion: mmss(nota.duracionMs) })}</span>
          <BotonEnviar etiqueta={error ? t('oficinaV2.voz.reintentar') : t('oficinaV2.voz.enviar')} cargando={enviando} onClick={() => void enviarNota(nota)} />
        </div>
      </Marco>
    );
  }

  return (
    <Marco error={error} pie={pie} onDragOver={(e) => e.preventDefault()} onDrop={alSoltar}>
      {adjuntos.length ? (
        <ul className="flex gap-2 overflow-x-auto px-3 pt-3">
          {adjuntos.map((a) => (
            <li key={a.chipId} className="relative shrink-0">
              {a.vista ? (
                <img src={a.vista} alt={a.nombre} className="h-16 w-16 rounded-o-s object-cover" />
              ) : (
                <span className="flex h-16 w-40 items-center gap-2 rounded-o-s bg-oficina-hover px-2 text-o-sub text-oficina-texto">
                  <FileText className="h-5 w-5 shrink-0 text-oficina-enlace" aria-hidden />
                  <span className="truncate">{a.nombre}</span>
                </span>
              )}
              {a.estado === 'subiendo' ? (
                <span className="absolute inset-0 flex items-center justify-center rounded-o-s bg-black/50">
                  <Loader2 className="h-5 w-5 animate-spin text-white" aria-label={t('oficinaV2.chat.subiendo')} />
                </span>
              ) : null}
              {a.estado === 'fallo' ? <span className="absolute inset-0 rounded-o-s ring-2 ring-oficina-error" aria-hidden /> : null}
              <button
                type="button"
                onClick={() => quitar(a.chipId)}
                aria-label={t('oficinaV2.chat.quitarAdjunto', { nombre: a.nombre })}
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-oficina-elevada text-oficina-texto shadow ring-1 ring-black/40"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-end gap-1 rounded-o-xl border border-white/[0.12] bg-oficina-fondo/40 px-1.5 py-1">
        <BotonIcono icono={Paperclip} etiqueta={t('oficinaV2.chat.adjuntar')} onClick={() => selector.current?.click()} />
        <input
          ref={selector}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) adjuntar(e.target.files);
            e.target.value = '';
          }}
        />
        <textarea
          ref={area}
          rows={1}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onPaste={alPegar}
          onKeyDown={(e) => {
            if (!e.nativeEvent.isComposing && shouldSendOnEnter(e, { isCoarsePointer: enterSaltaLinea })) {
              e.preventDefault();
              void enviar();
            }
          }}
          placeholder={placeholder ?? t('oficinaV2.chat.escribe')}
          aria-label={placeholder ?? t('oficinaV2.chat.escribe')}
          className="max-h-[200px] min-h-[40px] flex-1 resize-none bg-transparent px-1.5 py-[9px] text-o-cuerpo leading-[22px] text-oficina-texto outline-none placeholder:text-oficina-texto3"
        />
        {hayAlgo || subiendo ? (
          <BotonEnviar etiqueta={t('oficinaV2.chat.enviar')} cargando={enviando || subiendo} onClick={() => void enviar()} />
        ) : (
          <BotonIcono icono={Mic} etiqueta={t('oficinaV2.voz.nota')} onClick={() => void g.empezar()} className="text-oficina-texto2" />
        )}
      </div>
      {g.error ? <p className="px-4 pb-2 text-o-sub text-oficina-bloqueado">{t(g.error === 'sin_permiso' ? 'oficinaV2.voz.sinPermiso' : 'oficinaV2.voz.noSoportado')}</p> : null}
    </Marco>
  );
}

/**
 * Hueco bajo el compositor: lo que tapa el teclado si está abierto; si no, en el móvil, la barra de inicio del
 * iPhone (safe-area; 0 donde no hay). En escritorio la ventana del chat no llega al borde: nada.
 */
export function piePorDebajo(tecladoPx: number, movil: boolean): string | undefined {
  // En escritorio/iPad la ventana del chat flota y no llega al borde: el teclado no se compensa (revisión 09-10).
  if (!movil) return undefined;
  return tecladoPx > 0 ? `${tecladoPx}px` : 'env(safe-area-inset-bottom)';
}

function Marco({ error, pie, children, ...resto }: { error: string | null; pie: string | undefined; children: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    // 03-11 (Apple, como Mensajes): el campo es una cápsula con filo sobre la ventana, sin barra de borde a borde.
    <div data-compositor="" className="shrink-0 bg-oficina-elevada px-3 pb-3 pt-1.5 sm:px-5" style={pie ? { paddingBottom: pie } : undefined} {...resto}>
      {error ? (
        <p role="alert" className="px-4 pt-2 text-o-sub text-oficina-error">
          {error}
        </p>
      ) : null}
      {children}
    </div>
  );
}

function BotonEnviar({ etiqueta, onClick, cargando }: { etiqueta: string; onClick: () => void; cargando?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={cargando}
      aria-label={etiqueta}
      title={etiqueta}
      className={cn(
        // Enviar es actuar: círculo de oro con la flecha hacia arriba, como en Mensajes.
        'mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-oficina-acento text-oficina-acento-tinta transition-colors hover:bg-oficina-acento-hover disabled:opacity-60 outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
        TACTIL_44,
      )}
    >
      {cargando ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-[18px] w-[18px]" strokeWidth={2.75} />}
    </button>
  );
}
